import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, ClipboardType } from 'lucide-react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import styles from './ProjectTypeDrawer.module.css';

const ProjectTypeDrawer = ({ isOpen, onClose, onSave, projectType }) => {
  const [formData, setFormData] = useState({
    type_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (projectType) {
        fetchProjectTypeDetails(projectType.id);
      } else {
        setFormData({
          type_name: ''
        });
      }
    }
  }, [projectType, isOpen]);

  const fetchProjectTypeDetails = async (id) => {
    setFetching(true);
    try {
      const response = await api.get(`/project-types/${id}`);
      if (response.data.success) {
        setFormData({
          type_name: response.data.data.type_name || ''
        });
      }
    } catch (error) {
      console.error('Error fetching project type details:', error);
      toast.error('Failed to load project type details');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.type_name.trim()) {
      toast.error('Project type name is required');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (projectType) {
        response = await api.put(`/project-types/${projectType.id}`, formData);
      } else {
        response = await api.post('/project-types', formData);
      }

      if (response.data.success) {
        toast.success(projectType ? 'Project type updated successfully' : 'Project type created successfully');
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving project type:', error);
      toast.error(error.response?.data?.message || 'Failed to save project type');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={(e) => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{projectType ? 'Edit Project Type' : 'Add Project Type'}</h2>
            <p>{projectType ? 'Update details for this project category.' : 'Create a new category for projects.'}</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {fetching ? (
              <div className={styles['loading-state']} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                <Loader2 size={24} className={styles['spinner']} style={{ margin: '0 auto 12px' }} />
                <p>Loading project type details...</p>
              </div>
            ) : (
              <div className={styles['form-group']}>
                <label htmlFor="type_name">
                  <ClipboardType size={16} />
                  <span>Project Type Name <span className={styles['required-star']}>*</span></span>
                </label>
                <input
                  type="text"
                  id="type_name"
                  name="type_name"
                  placeholder="e.g. Residential, Industrial, Commercial"
                  value={formData.type_name}
                  onChange={handleChange}
                  autoFocus
                  required
                />
              </div>
            )}
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading || fetching}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{projectType ? 'Update Project Type' : 'Save Project Type'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectTypeDrawer;
